/** @jsxImportSource @emotion/react */

import { ChangeEvent, FC, ReactNode, useState } from "react";
import { css } from "@emotion/react";
import "@rmwc/top-app-bar/styles";
import {
  TopAppBar as MdTopAppBar,
  TopAppBarActionItem,
  TopAppBarFixedAdjust,
  TopAppBarNavigationIcon,
  TopAppBarRow,
  TopAppBarSection,
  TopAppBarTitle,
} from "@rmwc/top-app-bar";
import { default as menuIcon } from "@iconify/icons-mdi/menu";
import Svg from "../../Image/Svg";
import useConfigs from "../../ConfigContext/use-configs";
import Text from "../../DataDisplay/Text";
import Direction from "../../../commons/Direction";
import Menu, { MenuItemType, MenuType } from "../../DataDisplay/Menu/Menu";
import weatherNight from "@iconify/icons-mdi/weather-night";
import weatherSunny from "@iconify/icons-mdi/weather-sunny";
import { ThemeType } from "../../../commons/theme";
import Switches from "../../Form/Switches";
import arrowLeft from "@iconify/icons-mdi/arrow-left";
import { useHistory, useLocation } from "react-router-dom";
import useTheme from "../../../commons/hooks/use-theme";
import notifications from "@iconify/icons-mdi/notifications";
import Badges from "../../DataDisplay/Badges";
import useNotification from "../../../commons/hooks/use-notification";
import useNotificationIcon from "../../Misc/Notification/use-notification-icon";

export type AppBarProps = {
  title?: string;
  actionItems?: AppBarActionItemProps[];
  children?: (navOpen: boolean, closeNav: () => void, onItemClick?: (label: string) => void) => void;
  dir?: Direction;
  menu?: MenuType;
  topAppBarTitle?: TopAppBarTitleType[];
  showChangeTheme?: boolean;
  backAction?: boolean;
  backActionIgnorePaths?: string[];
};

type TopAppBarTitleType = {
  title?: string;
  icon?: any;
  iconSrc?: string;
  onClick?: () => void;
  align?: "start" | "end";
  showOnMobile: boolean;
  showChangeTheme?: boolean;
  className?: string;
  iconRender?: ReactNode;
  render?: () => ReactNode;
};

type AppBarActionItemProps = {
  icon?: object;
  iconSrc?: string;
  onClick?: () => void;
  showOnMobile: boolean;
  className?: string;
};

const TopAppBar: FC<AppBarProps> = (props) => {
  let configs = useConfigs();

  const theme = useTheme();

  const notification = useNotification();
  const { getNotificationIcon } = useNotificationIcon();

  const {
    actionItems,
    title,
    children,
    dir = configs.direction,
    menu,
    topAppBarTitle = [],
    backAction,
    backActionIgnorePaths,
  } = props;

  const history = useHistory();

  const location = useLocation();

  const [selectedTheme, setSelectedTheme] = useState(theme.type);

  const [shouldOpenNav, setShouldOpenNav] = useState(window.innerWidth > 768);

  const handleBackAction = () => history.goBack();

  const handleNavOpen = () => setShouldOpenNav((prevState) => !prevState);

  const handleChangeTheme = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedTheme = e.target.checked ? ThemeType.DARK : ThemeType.LIGHT;
    configs.setThemeType(selectedTheme);
    setSelectedTheme(selectedTheme);
  };

  const renderAppBarActionItems = () => {
    return (
      actionItems &&
      actionItems.map((item, idx) => {
        if (item.showOnMobile || window.innerWidth >= 768) {
          return (
            <TopAppBarActionItem
              key={idx}
              icon={<Svg icon={item.icon} src={item.iconSrc} color={theme.material.onPrimary} />}
              onClick={item.onClick}
              className={item.className}
            />
          );
        } else return null;
      })
    );
  };

  const renderBackAction = () => {
    if (backAction) {
      let pathname = location && location.pathname ? location.pathname : "";
      let showBackAction = true;
      if (backActionIgnorePaths && backActionIgnorePaths.length && backActionIgnorePaths.includes(pathname)) {
        showBackAction = false;
      }
      if (showBackAction) {
        return (
          <TopAppBarActionItem
            className="appBar-back-to-page"
            icon={<Svg icon={arrowLeft} color={theme.material.onPrimary} />}
            onClick={handleBackAction}
          />
        );
      }
      return null;
    }
    return null;
  };

  const renderTopAppBarNotifications = () => {
    if (!notification.items || !notification.items.length) return null;

    return (
      <Menu
        icon={
          <Badges label="" align="start" backgroundColor={theme.material.primary}>
            <Svg icon={notifications} size="small" color={theme.material.onPrimary} />
          </Badges>
        }
        title=""
        items={convertNotificationItemToMenuItem()}
        className="top-app-bar-notfication"
      />
    );
  };

  const convertNotificationItemToMenuItem = (): MenuItemType[] => {
    return notification.items.map((item) => ({
      key: item.key,
      title: item.title,
      description: item.message,
      icon: getNotificationIcon(item.type),
      onClick: () => item.onClick(true),
    }));
  };

  const renderAppBarMenu = () => {
    return (
      menu &&
      menu.title &&
      menu.items && (
        <Menu
          className={menu.className}
          items={menu.items}
          icon={<Svg icon={menu.icon} src={menu.iconSrc} size="medium" color={theme.material.onPrimary} />}
          title={menu.title}
          hideTitleInMobile={menu.hideTitleInMobile}
          onlyInMobile={menu.onlyInMobile}
        />
      )
    );
  };

  const renderTopAppBarTitleStart = () => {
    if (topAppBarTitle && topAppBarTitle.length) {
      return topAppBarTitle.map((item, index) => {
        if (item.showOnMobile || window.innerWidth >= 768) {
          if (item.showChangeTheme) {
            return (
              <div
                key={index}
                style={{ display: "flex", alignItems: "center", margin: "0 25px" }}
                className="theme-switch"
              >
                <Svg icon={weatherSunny} color={theme.material.onPrimary} />
                <Switches
                  checked={selectedTheme === ThemeType.DARK}
                  onChange={handleChangeTheme}
                  className="change-theme"
                />
                <Svg icon={weatherNight} color={theme.material.onPrimary} />
              </div>
            );
          } else if (item.align === "start") {
            return renderTopAppBarTitle(item, index);
          } else return null;
        } else return null;
      });
    }
    return null;
  };

  const renderTopAppBarTitle = (item: TopAppBarTitleType, index: number) => (
    <TopAppBarTitle onClick={item.onClick} key={index} className={item.className}>
      <div style={{ cursor: "pointer", marginLeft: 10 }}>
        <Text type={"paragraph"} themeType={ThemeType.DARK}>
          {item.iconRender}
          {item.render && item.render()}
          <Svg icon={item.icon} src={item.iconSrc} size="medium" color={theme.material.onPrimary} />
          {item.title}
        </Text>
      </div>
    </TopAppBarTitle>
  );

  const renderTopAppBarTitleEnd = () => {
    if (topAppBarTitle && topAppBarTitle.length) {
      return topAppBarTitle.map((item, index) => {
        if ((item.showOnMobile || window.innerWidth >= 768) && item.align === "end") {
          return renderTopAppBarTitle(item, index);
        } else return null;
      });
    }
    return null;
  };

  const styles = css`
    background: ${theme.appBarBackground};
  `;

  return (
    <>
      <MdTopAppBar fixed dir={dir} css={styles}>
        <TopAppBarRow>
          <TopAppBarSection alignStart>
            <TopAppBarNavigationIcon
              onClick={handleNavOpen}
              icon={<Svg icon={menuIcon} size={"medium"} color={theme.material.onPrimary} />}
              className="top-app-bar-navigation"
            />
            <TopAppBarTitle className="top-app-bar-title">
              <Text type={"paragraph"} themeType={ThemeType.DARK}>
                {title}
              </Text>
            </TopAppBarTitle>
            {renderTopAppBarTitleStart()}
          </TopAppBarSection>
          <TopAppBarSection alignEnd>
            {renderTopAppBarNotifications()}
            {renderTopAppBarTitleEnd()}
            {renderAppBarMenu()}
            {renderAppBarActionItems()}
            {renderBackAction()}
          </TopAppBarSection>
        </TopAppBarRow>
      </MdTopAppBar>
      <TopAppBarFixedAdjust />
      {children(shouldOpenNav, () => setShouldOpenNav(false))}
    </>
  );
};

export default TopAppBar;
